"use client";

import { useState } from "react";
import Link from "next/link";
import { Mail, Copy, Check, Send, Home, ChevronRight, MessageSquare, ShieldCheck } from "lucide-react";

export function ContactClient() {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.meazora.com";
  const domain = siteUrl.replace("https://", "").replace("http://", "");
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "feedback",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [honeypot, setHoneypot] = useState("");

  const emailAddress = `support@${domain}`;

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setStatus("error");
      return;
    }

    setStatus("submitting");

    // Honeypot check (Spam/Bot detection)
    if (honeypot) {
      if (process.env.NODE_ENV !== "production") console.warn(`🤖 [${siteName}] Bot detected! Honeypot field was filled. Discarding submission silently.`);
      setStatus("success");
      setFormData({ name: "", email: "", subject: "feedback", message: "" });
      setHoneypot("");
      return;
    }

    const accessKey = process.env.NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY;

    if (!accessKey) {
      if (process.env.NODE_ENV !== "production") console.warn(
        `⚠️ [${siteName}] NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY is not configured in environment variables. ` +
        "Simulating a successful submission in development/static export mode."
      );
      await new Promise((resolve) => setTimeout(resolve, 800));
      setStatus("success");
      setFormData({ name: "", email: "", subject: "feedback", message: "" });
      setHoneypot("");
      return;
    }

    try {
      const payload = {
        access_key: accessKey,
        name: formData.name,
        email: formData.email,
        subject: `[${siteName} Contact] - ${formData.subject || "feedback"}`,
        message: formData.message,
        from_name: `${siteName} Contact Form`,
      };

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setStatus("success");
        setFormData({ name: "", email: "", subject: "feedback", message: "" });
        setHoneypot("");
      } else {
        if (process.env.NODE_ENV !== "production") console.error("Contact form submission failed:", data.message || data);
        setStatus("error");
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Contact form network error:", err);
      setStatus("error");
    }
  };

  return (
    <div className="w-full flex flex-col items-center pt-8 pb-24 px-4 sm:px-6 gap-16 font-sans">
      {/* HTML Breadcrumbs navigation */}
      <nav className="w-full flex items-center gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800 pb-4 justify-start">
        <Link href="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-1 font-medium">
          <Home size={14} />
          <span>Home</span>
        </Link>
        <ChevronRight size={12} className="text-slate-300" />
        <span className="font-semibold text-slate-800 dark:text-slate-200 truncate">
          Contact
        </span>
      </nav>

      {/* Header section */}
      <header className="flex flex-col items-start text-left gap-3 w-full max-w-4xl mb-4 animate-in fade-in duration-500">
        <span className="text-[10px] font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400 font-sans">
          GET IN TOUCH
        </span>
        <h1 className="text-3xl sm:text-4xl md:text-5xl tracking-tight text-slate-900 dark:text-white leading-[1.1] font-sans">
          <span className="font-medium block">Contact</span>
          <span className="font-black block">{siteName}</span>
        </h1>
        <p className="text-slate-650 dark:text-slate-400 text-sm max-w-2xl leading-relaxed font-sans">
          Have feedback on a calculator, noticed an incorrect code standard, or want to request a custom estimator? Let us know. We review all submissions.
        </p>
      </header>

      {/* Main Grid Content */}
      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-5 gap-8 items-start">
        
        {/* Left Column: Direct Contacts */}
        <div className="md:col-span-2 flex flex-col gap-6">
          
          {/* Email Card */}
          <div className="p-6 rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.02)] flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 p-2.5 rounded-xl">
                <Mail size={18} />
              </div>
              <div>
                <h2 className="text-xs font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest font-mono">
                  EMAIL US DIRECTLY
                </h2>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  General Support &amp; Feedback
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 mt-2">
              <a 
                href={`mailto:${emailAddress}`}
                className="text-base font-bold text-blue-600 dark:text-blue-400 hover:underline break-all font-mono"
              >
                {emailAddress}
              </a>
              
              <button
                onClick={handleCopyEmail}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-slate-200 dark:border-zinc-805 bg-slate-50 dark:bg-zinc-950 text-slate-600 dark:text-slate-350 hover:bg-slate-100 dark:hover:bg-zinc-900 transition-colors text-xs font-bold"
              >
                {copied ? (
                  <>
                    <Check size={14} className="text-emerald-500" />
                    <span>Email Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={14} />
                    <span>Copy Email Address</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Response Promise Card */}
          <div className="p-6 rounded-2xl border border-slate-200/60 dark:border-zinc-850 bg-slate-50/50 dark:bg-zinc-900/40 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-slate-800 dark:text-zinc-205 font-bold text-sm">
              <MessageSquare size={16} className="text-blue-600 dark:text-blue-400" />
              <span>Response Time Guarantee</span>
            </div>
            <p className="text-xs text-slate-550 dark:text-slate-400 leading-relaxed">
              We review every ticket. For calculations errors or spec corrections, we typically audit the formulas and deploy updates within 24 to 48 hours of verification.
            </p>
          </div>

          {/* Editorial Standards Card */}
          <div className="p-6 rounded-2xl border border-slate-200/60 dark:border-zinc-850 bg-slate-50/50 dark:bg-zinc-900/40 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-slate-800 dark:text-zinc-205 font-bold text-sm">
              <ShieldCheck size={16} className="text-blue-600 dark:text-blue-400" />
              <span>Editorial Integrity</span>
            </div>
            <p className="text-xs text-slate-555 dark:text-slate-400 leading-relaxed">
              {siteName} calculators are built strictly from official construction authority publications (ACI, TCNA, GA, IRC). If you submit a correction, please include the relevant manual section or reference details.
            </p>
          </div>

        </div>

        {/* Right Column: Contact Form */}
        <div className="md:col-span-3 p-8 rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
          
          {status === "success" ? (
            <div className="flex flex-col items-center text-center py-12 gap-4 animate-in fade-in duration-300">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <Check size={24} strokeWidth={3} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Message Sent Successfully!
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm leading-relaxed">
                Thank you for reaching out. A copy of your message has been logged, and we will get back to you shortly.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="mt-4 px-6 py-2.5 rounded-xl bg-slate-100 dark:bg-zinc-950 text-slate-705 dark:text-slate-350 hover:bg-slate-200 dark:hover:bg-zinc-800 transition-colors text-xs font-bold"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                Send Us a Message
              </h2>

              {status === "error" && (
                <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/30 text-rose-700 dark:text-rose-450 text-xs font-semibold">
                  Please fill out all required fields before submitting.
                </div>
              )}

              {/* Honeypot field (Trap for bots) */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="country_checking">Please leave this field empty</label>
                <input
                  type="text"
                  id="country_checking"
                  name="country_checking"
                  tabIndex={-1}
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  autoComplete="off"
                />
              </div>

              {/* Name */}
              <div className="flex flex-col gap-1.5">
                <label 
                  htmlFor="name" 
                  className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider font-mono"
                >
                  Your Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-950 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-600 dark:focus:border-blue-500 transition-all"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label 
                  htmlFor="email" 
                  className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider font-mono"
                >
                  Email Address <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-950 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-600 dark:focus:border-blue-500 transition-all"
                />
              </div>

              {/* Subject */}
              <div className="flex flex-col gap-1.5">
                <label 
                  htmlFor="subject" 
                  className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider font-mono"
                >
                  Inquiry Type
                </label>
                <select
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-950 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-600 dark:focus:border-blue-500 transition-all"
                >
                  <option value="feedback">General Feedback</option>
                  <option value="bug">Report an Error or Bug</option>
                  <option value="feature">Request an Estimator</option>
                  <option value="advertising">Advertising &amp; Inquiries</option>
                </select>
              </div>

              {/* Message */}
              <div className="flex flex-col gap-1.5">
                <label 
                  htmlFor="message" 
                  className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider font-mono"
                >
                  Your Message <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe your inquiry in detail..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-950 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-600 dark:focus:border-blue-500 transition-all resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-slate-150 dark:hover:bg-slate-200 text-white dark:text-slate-900 font-bold transition-all text-sm active:scale-98 disabled:opacity-50"
              >
                <Send size={16} />
                <span>{status === "submitting" ? "Sending..." : "Submit Message"}</span>
              </button>
            </form>
          )}

        </div>

      </div>

      <div className="ticks my-12 w-full max-w-4xl" />

      {/* Contact & Support Guidelines */}
      <section className="w-full max-w-4xl text-left font-sans text-slate-700 dark:text-slate-300 space-y-6 pb-12">
        <h2 className="font-heading-condensed font-black text-xl md:text-2xl uppercase tracking-wide text-slate-900 dark:text-white">
          Contact &amp; Support Guidelines
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-500 dark:text-zinc-400">
          Our goal is to provide contractors, estimators, and DIY homeowners with the most accurate, reliable, and standards-compliant estimation tools available online. We take calculation bugs, formula errors, and code specification issues extremely seriously.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
          <div className="p-5 rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <h3 className="font-bold text-sm text-slate-900 dark:text-white mb-2 font-sans">
              Reporting Formula Bugs
            </h3>
            <p className="text-xs text-slate-500 dark:text-zinc-450 leading-relaxed font-sans">
              When reporting a discrepancy in a calculator's math, please provide the exact input values used, the expected outputs, and if possible, a link to the standard formula or code manual (e.g., ACI, IRC, TCNA) that you are referencing.
            </p>
          </div>
          <div className="p-5 rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <h3 className="font-bold text-sm text-slate-900 dark:text-white mb-2 font-sans">
              Suggesting New Tools
            </h3>
            <p className="text-xs text-slate-500 dark:text-zinc-450 leading-relaxed font-sans">
              We frequently expand our suite of calculators. If you need a specific model (e.g. for framing, grading, or electrical load calculations), submit a detailed description of the inputs and outputs needed, along with standard trade rules-of-thumb.
            </p>
          </div>
          <div className="p-5 rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <h3 className="font-bold text-sm text-slate-900 dark:text-white mb-2 font-sans">
              Privacy &amp; Advertising
            </h3>
            <p className="text-xs text-slate-500 dark:text-zinc-450 leading-relaxed font-sans">
              We operate under strict privacy guidelines. We do not sell user lead data. For advertising opportunities, sponsorship inquiries, or licensing requests, select the appropriate type in the dropdown or email us directly.
            </p>
          </div>
        </div>

        <h3 className="font-bold text-sm text-slate-900 dark:text-white pt-4 font-sans">
          Response Times &amp; Verification Process
        </h3>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-500 dark:text-zinc-400">
          Once we receive a calculation or bug report, our engineering and editorial teams review the ticket within 24 to 48 hours. Any verified mathematical errors are corrected, run through our Jest automated unit test suites, and deployed to production immediately. General feedback and feature suggestions are cataloged for monthly design sprints.
        </p>
      </section>
    </div>
  );
}
